import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Collapse, IconButton, useMediaQuery } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { SpinHistory } from '../../types';
import { colors } from '../../theme/themeConfig';
import DiscountItem from './DiscountItem';

interface DiscountListProps {
  discounts: SpinHistory[];
  cartTotal: number;
  onApplyDiscount: (discountId: string | null, amount: number) => void;
  activeDiscountId?: string | null;
}

/**
 * Simplified discount list component with checkboxes
 * Only one discount can be active at a time
 */
const DiscountList: React.FC<DiscountListProps> = ({
  discounts,
  cartTotal,
  onApplyDiscount,
  activeDiscountId: externalActiveDiscountId
}) => {
  const isMobile = useMediaQuery('(max-width: 375px)');
  const [expanded, setExpanded] = useState(false);
  const [internalActiveDiscountId, setInternalActiveDiscountId] = useState<string | null>(externalActiveDiscountId || null);
  
  // Use external activeDiscountId if provided, otherwise use internal state
  const activeDiscountId = externalActiveDiscountId !== undefined ? externalActiveDiscountId : internalActiveDiscountId;
  
  // Update internal state when external prop changes
  useEffect(() => {
    if (externalActiveDiscountId !== undefined) {
      setInternalActiveDiscountId(externalActiveDiscountId);
    }
  }, [externalActiveDiscountId]);
  
  // Toggle expanded state
  const toggleExpanded = () => {
    setExpanded(prev => !prev);
  };
  
  // Handle discount toggle
  const handleToggleDiscount = (discountId: string, isActive: boolean) => {
    if (isActive) {
      // Activate this discount
      setInternalActiveDiscountId(discountId);
      
      // Find the discount to get its amount
      const discount = discounts.find(d => d.id === discountId);
      if (discount) {
        onApplyDiscount(discountId, discount.reward.rewardAmount);
      }
    } else {
      // Deactivate discount
      setInternalActiveDiscountId(null);
      onApplyDiscount(null, 0);
    }
  };
  
  // Simple function to check if a discount meets minimum purchase requirement
  const meetsMinPurchase = (discount: SpinHistory): boolean => {
    return cartTotal >= discount.reward.minPurchase;
  };
  
  // No discounts to show
  if (discounts.length === 0) {
    return null;
  }
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        mb: 3, 
        p: 2,
        border: `1px solid ${colors.border.light}`,
        borderRadius: 2,
        backgroundColor: colors.background.card
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          cursor: 'pointer',
          mb: expanded ? 2 : 0
        }}
        onClick={toggleExpanded}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LocalOfferIcon 
            sx={{ 
              color: colors.secondary.main, 
              mr: 1,
              fontSize: isMobile ? 20 : 24
            }} 
          />
          <Typography 
            variant={isMobile ? "body1" : "h6"} 
            component="div" 
            sx={{ color: colors.text.primary }}
          >
            Доступные скидки ({discounts.length})
          </Typography>
        </Box>
        <IconButton size="small" sx={{ color: colors.text.primary }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      
      <Collapse in={expanded}>
        <Box>
          {discounts.map(discount => (
            <DiscountItem
              key={discount.id}
              discount={discount}
              isActive={activeDiscountId === discount.id}
              isDisabled={
                // Disabled if this discount doesn't meet min purchase requirement
                !meetsMinPurchase(discount) || 
                // Or if another discount is active
                (activeDiscountId !== null && activeDiscountId !== discount.id)
              }
              onToggle={handleToggleDiscount}
            />
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default DiscountList;
